import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import Home from './pages/Home';
import Hosting from './pages/Hosting';
import Domains from './pages/Domains';
import DomainSearchResults from './pages/DomainSearchResults';
import VPS from './pages/VPS';
import Email from './pages/Email';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import Dashboard from './pages/Dashboard';
import Orders from './pages/dashboard/Orders';
import Support from './pages/dashboard/Support';
import Billing from './pages/dashboard/Billing';
import Profile from './pages/dashboard/Profile';
import DashboardDomains from './pages/dashboard/Domains';
import Admin from './pages/Admin';
import AdminLogin from './pages/admin/AdminLogin';
import VncConsole from './pages/VncConsole';
import TermConsole from './pages/TermConsole';
import AdminRoute from './components/admin/AdminRoute';
import About from './pages/About';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import AcceptableUse from './pages/legal/AcceptableUse';
import Refund from './pages/legal/Refund';
import SLA from './pages/legal/SLA';
import CookiePolicy from './pages/legal/CookiePolicy';
import DomainTransfer from './pages/DomainTransfer';
import DomainTransferHosting from './pages/DomainTransferHosting';
import HostingCheckout from './pages/checkout/HostingCheckout';
import EmailCheckout from './pages/checkout/EmailCheckout';
import DomainCheckout from './pages/checkout/DomainCheckout';
import DomainTransferCheckout from './pages/checkout/DomainTransferCheckout';
import VPSDashboard from './pages/VPSDashboard';
import EmailDashboard from './pages/EmailDashboard';
import EmailSetup from './pages/EmailSetup';
import EmailMigrate from './pages/EmailMigrate';
import EmailMailboxes from './pages/EmailMailboxes';
import BusinessEmailOnboarding from './pages/BusinessEmailOnboarding';
import ConnectDevices from './pages/email/ConnectDevices';
import AdvancedVPSDashboard from './components/customer/AdvancedVPSDashboard';
import EnhancedVPSInstanceDetail from './components/customer/EnhancedVPSInstanceDetail';
import HostingDashboard from './components/customer/HostingDashboard';
import HostingAccountDetail from './components/customer/HostingAccountDetail';
import StreamlinedVPSCheckout from './components/customer/StreamlinedVPSCheckout';
import { AuthProvider } from './contexts/AuthContext';

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Admin Routes - No Header/Footer */}
          <Route path="/admin" element={
            <AdminRoute>
              <Admin />
            </AdminRoute>
          } />
          <Route path="/admin/*" element={
            <AdminRoute>
              <Admin />
            </AdminRoute>
          } />

          {/* noVNC console — full-screen, no header/footer */}
          <Route path="/vnc/:sessionId" element={<VncConsole />} />
          <Route path="/term/:sessionId" element={<TermConsole />} />

          {/* Main Website Routes - With Header/Footer */}
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="hosting" element={<Hosting />} />
            <Route path="domains" element={<Domains />} />
            <Route path="domain-search/:domainName" element={<DomainSearchResults />} />
            <Route path="vps" element={<VPS />} />
            <Route path="email" element={<Email />} />
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="dashboard/orders" element={<Orders />} />
            <Route path="dashboard/support" element={<Support />} />
            <Route path="dashboard/billing" element={<Billing />} />
            <Route path="dashboard/profile" element={<Profile />} />
            <Route path="dashboard/domains" element={<DashboardDomains />} />
            <Route path="about" element={<About />} />
            <Route path="contact" element={<Contact />} />
            <Route path="domain-transfer" element={<DomainTransfer />} />
            <Route path="domain-transfer-hosting" element={<DomainTransferHosting />} />
            <Route path="checkout/hosting/:packageId" element={<HostingCheckout />} />
            {/* Legacy VPS checkout charged the stale hosting_packages catalog
                and nothing links to it anymore — send old bookmarks to the
                live vps_plans-backed deploy flow instead. */}
            <Route path="checkout/vps/:packageId" element={<Navigate to="/vps/deploy" replace />} />
            <Route path="checkout/email/:packageId" element={<EmailCheckout />} />
            <Route path="checkout/domain/:domainName" element={<DomainCheckout />} />
            <Route path="checkout/domain-transfer" element={<DomainTransferCheckout />} />
            <Route path="vps-dashboard" element={<VPSDashboard />} />
            <Route path="email-dashboard" element={<EmailDashboard />} />
            {/* /customer/email is the canonical name we link from the
                welcome email + several wizard back-buttons. Alias it to
                the same EmailDashboard so old bookmarks resolve too. */}
            <Route path="customer/email" element={<EmailDashboard />} />
            {/* Post-purchase onboarding wizard — the new 9-step walker that
                guides the customer through role / fresh-vs-migrate / source /
                cutover-timing / DNS / mailboxes / migration / cutover / done.
                Wraps the individual EmailSetup/Mailboxes/Migrate pages inline. */}
            <Route path="dashboard/email/onboard/:id" element={<BusinessEmailOnboarding />} />

            {/* Post-purchase email setup wizard — DNS records to paste,
                live verification, provisioning timeline.
                v2 path matched first because ':domainId' would otherwise eat 'v2'. */}
            <Route path="dashboard/email/setup/v2/:id" element={<EmailSetup />} />
            <Route path="dashboard/email/setup/:domainId" element={<EmailSetup />} />
            {/* v2 migrate path first so ':domainId' doesn't swallow 'v2'. */}
            <Route path="dashboard/email/migrate/v2/:id" element={<EmailMigrate />} />
            <Route path="dashboard/email/migrate/:domainId" element={<EmailMigrate />} />
            {/* v2 "Connect your devices" — per-client IMAP/SMTP guides + iOS
                profile download. v2-only (mx1), so no v1 sibling. */}
            <Route path="dashboard/email/connect/v2/:id" element={<ConnectDevices />} />
            {/* v2 mailbox/alias management for Business Email v2 (mx1) — registered
                before the v1 :domainId route so 'v2' literal isn't captured as id. */}
            <Route path="dashboard/email/mailboxes/v2/:id" element={<EmailMailboxes />} />
            <Route path="dashboard/email/mailboxes/:domainId" element={<EmailMailboxes />} />
            
            {/* Customer VPS Management Routes */}
            <Route path="customer/vps" element={<AdvancedVPSDashboard />} />
            <Route path="customer/vps/:id" element={<EnhancedVPSInstanceDetail />} />
            <Route path="vps/deploy" element={<StreamlinedVPSCheckout />} />

            {/* Customer Hosting Management Routes */}
            <Route path="customer/hosting" element={<HostingDashboard />} />
            <Route path="customer/hosting/:id" element={<HostingAccountDetail />} />

            {/* Legal & policy pages */}
            <Route path="terms" element={<Terms />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="aup" element={<AcceptableUse />} />
            <Route path="refund" element={<Refund />} />
            <Route path="sla" element={<SLA />} />
            <Route path="cookies" element={<CookiePolicy />} />

            {/* 404 catch-all — must be last */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;